/**
 * Built-in Project Board plugins. Toggle state is loaded from the API (`GET /app-config`)
 * and updated by administrators (`PUT /admin/app-config`). The API reads effective flags
 * for server-side behavior (e.g. change history); clients still send `X-PB-Plugins` for compatibility.
 */

export const PLUGIN_IDS = {
  CHANGE_HISTORY: "changeHistory",
  EMAIL_TRIGGERS: "emailTriggers",
  USER_MATRIX: "userMatrix",
  USER_IMPERSONATION: "userImpersonation",
  PROJECT_COMMENTS: "projectComments",
  FIELD_GROUPING: "fieldGrouping",
  AI_CHAT_PORTFOLIO: "aiChatPortfolio",
  MARKETPLACE: "marketplace",
  CONTRACTS: "contracts",
  CRM: "crm",
  INTERCONNECTS: "interconnects",
  REPORTING: "reporting",
  MATERIALS_INVENTORY: "materialsInventory",
  PERMIT_PLAN_MATERIAL_EXTRACTION: "permitPlanMaterialExtraction",
  DATACENTER_LAYOUT: "datacenterLayout",
};

const DEFAULT_PLUGINS_STATE = () => ({
  [PLUGIN_IDS.CHANGE_HISTORY]: true,
  [PLUGIN_IDS.EMAIL_TRIGGERS]: true,
  [PLUGIN_IDS.USER_MATRIX]: true,
  [PLUGIN_IDS.USER_IMPERSONATION]: true,
  [PLUGIN_IDS.PROJECT_COMMENTS]: true,
  [PLUGIN_IDS.FIELD_GROUPING]: true,
  [PLUGIN_IDS.AI_CHAT_PORTFOLIO]: false,
  [PLUGIN_IDS.MARKETPLACE]: false,
  [PLUGIN_IDS.CONTRACTS]: false,
  [PLUGIN_IDS.CRM]: false,
  [PLUGIN_IDS.INTERCONNECTS]: false,
  [PLUGIN_IDS.REPORTING]: false,
  [PLUGIN_IDS.MATERIALS_INVENTORY]: false,
  [PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION]: true,
  [PLUGIN_IDS.DATACENTER_LAYOUT]: true,
});

/** In-memory snapshot; hydrated after login from `GET /app-config`. */
let pluginsState = DEFAULT_PLUGINS_STATE();

/** Metadata for the Plugins UI (order = display order). */
export const PLUGIN_REGISTRY = [
  {
    id: PLUGIN_IDS.CHANGE_HISTORY,
    name: "Change history",
    description:
      "Records field-level changes (grid, API, imports), syncs legacy ACF change_log into the audit table, and shows embedded + database change history on each project page.",
  },
  {
    id: PLUGIN_IDS.EMAIL_TRIGGERS,
    name: "Email triggers",
    description:
      "Configure milestone notifications (recipients per trigger, subject/body templates). Sending email is prepared for future automation.",
  },
  {
    id: PLUGIN_IDS.USER_MATRIX,
    name: "User Matrix",
    description:
      "Define user types, assign users, and restrict which grid columns each type can see on Commercial and Expansion. Invitations use a shareable link until email delivery is automated.",
  },
  {
    id: PLUGIN_IDS.USER_IMPERSONATION,
    name: "User Impersonation",
    description:
      "Allows administrators to preview role-based visibility and capabilities by impersonating a user's role until exiting impersonation mode.",
  },
  {
    id: PLUGIN_IDS.PROJECT_COMMENTS,
    name: "Project comments",
    description:
      "Threaded comments on each project with file attachments, @mentions and reply notifications by email, and a Messages inbox for your posts and mentions.",
  },
  {
    id: PLUGIN_IDS.FIELD_GROUPING,
    name: "Field Grouping",
    description:
      "Master catalog of project fields/columns and logical groups used to manage table applicability and future department mapping.",
  },
  {
    id: PLUGIN_IDS.AI_CHAT_PORTFOLIO,
    name: "Portfolio AI assistant",
    description:
      "Floating chat on the Commercial project grid for portfolio Q&A. Configure API key and model under Settings → AI (administrators). Access: Administrator, Coordinator, and Manager roles; daily per-user limit.",
  },
  {
    id: PLUGIN_IDS.MARKETPLACE,
    name: "Marketplace",
    description:
      "Earn V-coins for completing work — logging in, uploading assigned files, commenting on projects, and more. Spend coins in the marketplace (coming soon).",
  },
  {
    id: PLUGIN_IDS.CONTRACTS,
    name: "Contracts",
    description:
      "Track construction and commercial contracts, link them to projects, and mark work as awarded or not yet awarded.",
  },
  {
    id: PLUGIN_IDS.CRM,
    name: "CRM",
    description:
      "Companies, people, and sales opportunities before a work order exists. Close Won generates the work order and starts the commercial phase process. Access: Sales, Coordinators, and Administrators.",
  },
  {
    id: PLUGIN_IDS.INTERCONNECTS,
    name: "Interconnects",
    description:
      "Relational CDT interconnect records for expansion subsegments. Adds location, GPS, partners, and associated segment links in the Engineering section when CDT appears in the project title.",
  },
  {
    id: PLUGIN_IDS.REPORTING,
    name: "Reporting",
    description:
      "Budget and portfolio analytics for expansion and commercial projects. Administrators, managers, and accountants can view expansion segment budget charts and tables.",
  },
  {
    id: PLUGIN_IDS.MATERIALS_INVENTORY,
    name: "Materials inventory",
    description:
      "Portfolio-wide materials table for commercial and expansion projects. Administrators, coordinators, drafters, engineering managers, and inventory specialists can view permit material lines and totals.",
  },
  {
    id: PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION,
    name: "Permit plan material extraction",
    description:
      "When a permit plan PDF is uploaded, read material names and quantities from the plan and fill the permit materials repeater. Shows a verify banner until a drafter confirms the totals.",
  },
  {
    id: PLUGIN_IDS.DATACENTER_LAYOUT,
    name: "Datacenter layout",
    description:
      "Fiber data-center floor planner: draw room outlines, set rack/aisle clearances, place pillars and vents, and save reusable layouts. Access: Network Managers and Administrators.",
  },
];

/** Replace plugin flags from API (shared for all users). Missing keys default to enabled. */
export function hydratePluginsFromServer(plugins) {
  const p = plugins && typeof plugins === "object" ? plugins : {};
  const flag = (id) =>
    Object.prototype.hasOwnProperty.call(p, id) ? Boolean(p[id]) : true;
  pluginsState = {
    [PLUGIN_IDS.CHANGE_HISTORY]: flag(PLUGIN_IDS.CHANGE_HISTORY),
    [PLUGIN_IDS.EMAIL_TRIGGERS]: flag(PLUGIN_IDS.EMAIL_TRIGGERS),
    [PLUGIN_IDS.USER_MATRIX]: flag(PLUGIN_IDS.USER_MATRIX),
    [PLUGIN_IDS.USER_IMPERSONATION]: flag(PLUGIN_IDS.USER_IMPERSONATION),
    [PLUGIN_IDS.PROJECT_COMMENTS]: flag(PLUGIN_IDS.PROJECT_COMMENTS),
    [PLUGIN_IDS.FIELD_GROUPING]: flag(PLUGIN_IDS.FIELD_GROUPING),
    [PLUGIN_IDS.AI_CHAT_PORTFOLIO]: flag(PLUGIN_IDS.AI_CHAT_PORTFOLIO),
    [PLUGIN_IDS.MARKETPLACE]: flag(PLUGIN_IDS.MARKETPLACE),
    [PLUGIN_IDS.CONTRACTS]: flag(PLUGIN_IDS.CONTRACTS),
    [PLUGIN_IDS.CRM]: flag(PLUGIN_IDS.CRM),
    [PLUGIN_IDS.INTERCONNECTS]: flag(PLUGIN_IDS.INTERCONNECTS),
    [PLUGIN_IDS.REPORTING]: flag(PLUGIN_IDS.REPORTING),
    [PLUGIN_IDS.MATERIALS_INVENTORY]: flag(PLUGIN_IDS.MATERIALS_INVENTORY),
    [PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION]: flag(PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION),
    [PLUGIN_IDS.DATACENTER_LAYOUT]: flag(PLUGIN_IDS.DATACENTER_LAYOUT),
  };
  window.dispatchEvent(new CustomEvent("pb-plugins-changed"));
}

export function clearPluginsHydration() {
  pluginsState = DEFAULT_PLUGINS_STATE();
}

/** @returns {boolean} Defaults to enabled for every known plugin until hydrated. */
export function isPluginEnabled(pluginId) {
  return Boolean(pluginsState[pluginId]);
}

/** Merge into axios / fetch headers so the API can align client hints with stored flags. */
export function buildPluginsHeader() {
  return {
    "X-PB-Plugins": JSON.stringify({
      changeHistory: isPluginEnabled(PLUGIN_IDS.CHANGE_HISTORY),
      emailTriggers: isPluginEnabled(PLUGIN_IDS.EMAIL_TRIGGERS),
      userMatrix: isPluginEnabled(PLUGIN_IDS.USER_MATRIX),
      userImpersonation: isPluginEnabled(PLUGIN_IDS.USER_IMPERSONATION),
      projectComments: isPluginEnabled(PLUGIN_IDS.PROJECT_COMMENTS),
      fieldGrouping: isPluginEnabled(PLUGIN_IDS.FIELD_GROUPING),
      aiChatPortfolio: isPluginEnabled(PLUGIN_IDS.AI_CHAT_PORTFOLIO),
      marketplace: isPluginEnabled(PLUGIN_IDS.MARKETPLACE),
      contracts: isPluginEnabled(PLUGIN_IDS.CONTRACTS),
      crm: isPluginEnabled(PLUGIN_IDS.CRM),
      interconnects: isPluginEnabled(PLUGIN_IDS.INTERCONNECTS),
      reporting: isPluginEnabled(PLUGIN_IDS.REPORTING),
      materialsInventory: isPluginEnabled(PLUGIN_IDS.MATERIALS_INVENTORY),
      permitPlanMaterialExtraction: isPluginEnabled(PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION),
      datacenterLayout: isPluginEnabled(PLUGIN_IDS.DATACENTER_LAYOUT),
    }),
  };
}
